import { TABS_DATA } from '@cont/Compete/constants';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import { useEffect, useState } from 'react';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import Compete from '@icon/Compete';
import throttle from 'lodash/throttle';
import { useIsMobile } from '@stores/AppCommonData';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import useCompeteInfo from '@cont/Compete/useCompeteInfo';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { getAnimationPath } from '@utils/animationHelpers';
import segmentStorage from '@events/segmentStorage';
import CompeteTabs from './Tabs/CompeteTabs';

const HeaderWrapper = styled.div`
  position: sticky;
  top: 72px;
  background-color: var(--color-base-0);
  z-index: 1;
  padding-top: ${Spacings.SPACING_8B};

  ${mediaQuery.uptoMobile} {
    top: 48px;
    margin: 0 -${Spacings.SPACING_4B};
    padding: 0 ${Spacings.SPACING_4B};
    padding-top: ${Spacings.SPACING_4B};
  }
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  height: ${({ $isScrolled }) => ($isScrolled ? '40px' : '68px')};
  margin-bottom: ${({ $isScrolled }) =>
    $isScrolled ? `${Spacings.SPACING_4B}` : `${Spacings.SPACING_8B}`};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_2B};
    height: ${({ $isScrolled }) => ($isScrolled ? '24px' : '68px')};
  }
  transition: all 0.3s;
`;

const StyledP2 = styled(Typography)`
  margin-top: ${Spacings.SPACING_2B};
  color: var(--color-text-secondary);
`;

const StyledP3 = styled(Typography)`
  color: ${({ $isDarkModeOn }) =>
    $isDarkModeOn
      ? 'var(--color-text-secondary-light)'
      : 'var(--color-i-blue-43)'};
`;

const StyledButton = styled(Button)`
  width: 118px;
  padding: 0;
  height: 40px;
  margin-left: ${Spacings.SPACING_4B};

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-left: 0;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: var(--color-base-1);
    width: 100%;
    padding: ${Spacings.SPACING_4B};
    border: 1px solid var(--color-divider);
    z-index: 1;
  }
`;

const Title = styled.div`
  margin-left: ${Spacings.SPACING_6B};
`;

const IconWrapper = styled.div`
  width: ${({ $isScrolled }) => ($isScrolled ? '40px' : '56px')};
  height: ${({ $isScrolled }) => ($isScrolled ? '40px' : '56px')};
  ${mediaQuery.uptoMobile} {
    width: 24px;
    height: 24px;
    margin-right: ${Spacings.SPACING_2B};
  }
`;

const AnimationContainer = styled(LottieAnimation)`
  height: 40px;
  width: 16px;
  margin-right: ${Spacings.SPACING_6};
`;

const Header = ({ handleChange, selectedTabIndex }) => {
  const isMobile = useIsMobile();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const [tabsData, setTabsData] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const { userCount } = useCompeteInfo();
  const router = useRouter();
  const {
    query: { goalSlug, goalUID }
  } = router;

  useEffect(() => {
    if (!isLoggedIn) {
      return setTabsData(TABS_DATA.slice(1));
    }
    return setTabsData(TABS_DATA);
  }, [isLoggedIn]);

  const hideSubHeadingOnScroll = () => {
    if (document.documentElement.scrollTop > 156) {
      return setIsScrolled(true);
    }
    return setIsScrolled(false);
  };

  useEffect(() => {
    const throttledScroll = throttle(hideSubHeadingOnScroll, 150);

    document.addEventListener('scroll', throttle(throttledScroll, 500), {
      passive: true
    });

    return () =>
      document.removeEventListener('scroll', throttle(throttledScroll, 500));
  }, [isMobile]);

  const getIconSize = () => {
    if (isMobile) {
      return { width: 24, height: 24 };
    }
    if (isScrolled) {
      return { width: 40, height: 40 };
    }
    return { width: 56, height: 56 };
  };

  const appDownloadLink = useOneLinkSmartScriptV2();
  const handleClick = () => {
    if (isMobile) {
      return window.open(appDownloadLink, '_blank');
    }
    segmentStorage.setItem('lps', 'Compete');
    return pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/compete/battlefield',
      query: {
        goalSlug,
        goalUID
      }
    });
  };
  const renderHeader = () => {
    if (isMobile) {
      return (
        <FlexColumn>
          <Flex>
            <IconWrapper>
              <Compete
                width={getIconSize().width}
                height={getIconSize().height}
              />
            </IconWrapper>

            <Typography variant={isScrolled ? 'h4' : 'h2'}>Compete</Typography>
          </Flex>

          {!isScrolled && (
            <StyledP2 variant="p2" as="div">
              Play against other learners to improve your rating
            </StyledP2>
          )}
        </FlexColumn>
      );
    }
    return (
      <Flex>
        <IconWrapper $isScrolled={isScrolled}>
          <Compete width={getIconSize().width} height={getIconSize().height} />
        </IconWrapper>

        <Title>
          <Typography variant={isScrolled ? 'h4' : 'h2'}>Compete</Typography>
          {!isScrolled && (
            <StyledP2 variant="p2">
              Play against other learners to improve your rating
            </StyledP2>
          )}
        </Title>
      </Flex>
    );
  };

  return (
    <HeaderWrapper>
      <Wrapper $isScrolled={isScrolled}>
        {renderHeader()}

        <ActionContainer>
          {userCount && (
            <Flex>
              <AnimationContainer
                src={getAnimationPath('competeJoiningMembers.json')}
                style={{ height: '16px', width: '40px' }}
              />

              <StyledP3 variant="p3" $isDarkModeOn={isDarkModeOn}>
                {userCount} learners competing
              </StyledP3>
            </Flex>
          )}

          <StyledButton size="small" onClick={handleClick}>
            {isMobile ? 'Download the app' : 'Compete now'}
          </StyledButton>
        </ActionContainer>
      </Wrapper>
      <CompeteTabs
        tabsData={tabsData}
        onChange={handleChange}
        selectedTabIndex={selectedTabIndex}
      />
    </HeaderWrapper>
  );
};

export default Header;
